// User Permissions System
// Defines permissions for different user roles

export const USER_ROLES = {
  VIEWER: 'viewer',
  MANAGER: 'manager',
  SUPPLIER: 'supplier'
};

export const PERMISSIONS = {
  // Dashboard permissions
  VIEW_DASHBOARD: 'view_dashboard',
  
  // Orders permissions
  VIEW_ORDERS: 'view_orders',
  CREATE_ORDER: 'create_order',
  EDIT_ORDER: 'edit_order',
  DELETE_ORDER: 'delete_order',
  MANAGE_ORDER_STATUS: 'manage_order_status',
  
  // Products permissions
  VIEW_PRODUCTS: 'view_products',
  ADD_PRODUCTS: 'add_products',
  EDIT_PRODUCTS: 'edit_products',
  DELETE_PRODUCTS: 'delete_products',
  BULK_UPLOAD_PRODUCTS: 'bulk_upload_products',
  
  // Suppliers permissions
  VIEW_SUPPLIERS: 'view_suppliers',
  ADD_SUPPLIERS: 'add_suppliers',
  EDIT_SUPPLIERS: 'edit_suppliers',
  DELETE_SUPPLIERS: 'delete_suppliers',
  
  // Reviews permissions
  VIEW_REVIEWS: 'view_reviews',
  RESPOND_TO_REVIEWS: 'respond_to_reviews',
  DELETE_REVIEWS: 'delete_reviews',
  
  // Chat permissions
  ACCESS_CHAT: 'access_chat',
  
  // Notifications permissions
  VIEW_ADMIN_NOTIFICATIONS: 'view_admin_notifications',
  
  // Organizer management
  MANAGE_ORGANIZERS: 'manage_organizers',
  ADD_ORGANIZERS: 'add_organizers',
  UPDATE_ORGANIZERS: 'update_organizers',
  
  // Profile permissions
  VIEW_PROFILE: 'view_profile',
  EDIT_PROFILE: 'edit_profile'
};

// Define permissions for each role
export const ROLE_PERMISSIONS = {
  [USER_ROLES.VIEWER]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_ORDERS,
    PERMISSIONS.VIEW_PRODUCTS,
    PERMISSIONS.VIEW_SUPPLIERS,
    PERMISSIONS.VIEW_REVIEWS,
    PERMISSIONS.VIEW_PROFILE,
    PERMISSIONS.EDIT_PROFILE
  ],
  
  [USER_ROLES.MANAGER]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_ORDERS,
    PERMISSIONS.CREATE_ORDER,
    PERMISSIONS.EDIT_ORDER,
    PERMISSIONS.DELETE_ORDER,
    PERMISSIONS.MANAGE_ORDER_STATUS,
    PERMISSIONS.VIEW_PRODUCTS,
    PERMISSIONS.ADD_PRODUCTS,
    PERMISSIONS.EDIT_PRODUCTS,
    PERMISSIONS.DELETE_PRODUCTS,
    PERMISSIONS.BULK_UPLOAD_PRODUCTS,
    PERMISSIONS.VIEW_SUPPLIERS,
    PERMISSIONS.ADD_SUPPLIERS,
    PERMISSIONS.EDIT_SUPPLIERS,
    PERMISSIONS.DELETE_SUPPLIERS,
    PERMISSIONS.VIEW_REVIEWS,
    PERMISSIONS.RESPOND_TO_REVIEWS,
    PERMISSIONS.DELETE_REVIEWS,
    PERMISSIONS.VIEW_PROFILE,
    PERMISSIONS.EDIT_PROFILE
  ],

  [USER_ROLES.SUPPLIER]: [
    // Suppliers have full access to all features
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_ORDERS,
    PERMISSIONS.CREATE_ORDER,
    PERMISSIONS.EDIT_ORDER,
    PERMISSIONS.DELETE_ORDER,
    PERMISSIONS.MANAGE_ORDER_STATUS,
    PERMISSIONS.VIEW_PRODUCTS,
    PERMISSIONS.ADD_PRODUCTS,
    PERMISSIONS.EDIT_PRODUCTS,
    PERMISSIONS.DELETE_PRODUCTS,
    PERMISSIONS.BULK_UPLOAD_PRODUCTS,
    PERMISSIONS.VIEW_SUPPLIERS,
    PERMISSIONS.ADD_SUPPLIERS,
    PERMISSIONS.EDIT_SUPPLIERS,
    PERMISSIONS.DELETE_SUPPLIERS,
    PERMISSIONS.VIEW_REVIEWS,
    PERMISSIONS.RESPOND_TO_REVIEWS,
    PERMISSIONS.DELETE_REVIEWS,
    PERMISSIONS.ACCESS_CHAT,
    PERMISSIONS.VIEW_ADMIN_NOTIFICATIONS,
    PERMISSIONS.MANAGE_ORGANIZERS,
    PERMISSIONS.ADD_ORGANIZERS,
    PERMISSIONS.UPDATE_ORGANIZERS,
    PERMISSIONS.VIEW_PROFILE,
    PERMISSIONS.EDIT_PROFILE
  ]
};

// Helper functions
export const getUserRole = () => {
  const userData = localStorage.getItem('user');
  if (userData) {
    const user = JSON.parse(userData);
    return user.organizer_access || "supplier";
  }
  return "supplier";
};

export const hasPermission = (permission) => {
  const userRole = getUserRole();
  const rolePermissions = ROLE_PERMISSIONS[userRole] || [];
  return rolePermissions.includes(permission);
};

export const checkMultiplePermissions = (permissions) => {
  return permissions.every(permission => hasPermission(permission));
};

export const getUserPermissions = (role = null) => {
  const userRole = role || getUserRole();
  return ROLE_PERMISSIONS[userRole] || [];
};

export const getPermissionDisplayData = (t) => {
  const userRole = getUserRole();
  const userPermissions = getUserPermissions();
  
  return [
    {
      title: t('permissions.dashboard.title'),
      description: t('permissions.dashboard.description'),
      isGranted: userPermissions.includes(PERMISSIONS.VIEW_DASHBOARD)
    },
    {
      title: t('permissions.orders.view.title'),
      description: t('permissions.orders.view.description'),
      isGranted: userPermissions.includes(PERMISSIONS.VIEW_ORDERS)
    },
    {
      title: t('permissions.orders.manage.title'),
      description: t('permissions.orders.manage.description'),
      isGranted: userPermissions.includes(PERMISSIONS.EDIT_ORDER)
    },
    {
      title: t('permissions.products.view.title'),
      description: t('permissions.products.view.description'),
      isGranted: userPermissions.includes(PERMISSIONS.VIEW_PRODUCTS)
    },
    {
      title: t('permissions.products.manage.title'),
      description: t('permissions.products.manage.description'),
      isGranted: userPermissions.includes(PERMISSIONS.ADD_PRODUCTS)
    },
    {
      title: t('permissions.suppliers.view.title'),
      description: t('permissions.suppliers.view.description'),
      isGranted: userPermissions.includes(PERMISSIONS.VIEW_SUPPLIERS)
    },
    {
      title: t('permissions.suppliers.manage.title'),
      description: t('permissions.suppliers.manage.description'),
      isGranted: userPermissions.includes(PERMISSIONS.ADD_SUPPLIERS)
    },
    {
      title: t('permissions.reviews.view.title'),
      description: t('permissions.reviews.view.description'),
      isGranted: userPermissions.includes(PERMISSIONS.VIEW_REVIEWS)
    },
    {
      title: t('permissions.reviews.manage.title'),
      description: t('permissions.reviews.manage.description'),
      isGranted: userPermissions.includes(PERMISSIONS.RESPOND_TO_REVIEWS)
    },
    {
      title: t('permissions.chat.title'),
      description: t('permissions.chat.description'),
      isGranted: userPermissions.includes(PERMISSIONS.ACCESS_CHAT)
    },
    {
      title: t('permissions.notifications.title'),
      description: t('permissions.notifications.description'),
      isGranted: userPermissions.includes(PERMISSIONS.VIEW_ADMIN_NOTIFICATIONS)
    },
    {
      title: t('permissions.organizers.title'),
      description: t('permissions.organizers.description'),
      isGranted: userPermissions.includes(PERMISSIONS.MANAGE_ORGANIZERS)
    }
  ];
};

export default {
  USER_ROLES,
  PERMISSIONS,
  ROLE_PERMISSIONS,
  getUserRole,
  hasPermission,
  checkMultiplePermissions,
  getUserPermissions,
  getPermissionDisplayData
};
